import { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { employeeOnboardingGateway } from '../index';
import { OnboardingLayout, type LayoutNavigation } from '../components/OnboardingLayout';
import { reachableSectionIndex, reachableTaxIndex } from '../progress';
import { FederalSection } from '../taxes/FederalSection';
import { StateSection } from '../taxes/StateSection';
import { TaxSettingsSection } from '../taxes/TaxSettingsSection';
import { TaxSummarySection } from '../taxes/TaxSummarySection';
import type { TaxSectionProps } from '../taxes/taxProps';
import {
  DOCUMENTS_PLACEHOLDER_PATH,
  ONBOARDING_SECTIONS,
  TAX_SECTIONS,
  sectionPath,
  taxSectionPath,
  type EmployeeSetupResponse,
  type TaxSectionSlug,
  type TaxSetupResponse,
} from '../types';

const SECTION_COMPONENTS: Record<TaxSectionSlug, ComponentType<TaxSectionProps>> = {
  federal: FederalSection,
  state: StateSection,
  'tax-settings': TaxSettingsSection,
  'tax-summary': TaxSummarySection,
};

/** `/employee/taxes/:section` (Step 2). The route guard in App.tsx has already established an authenticated Employee session. */
export function EmployeeTaxesPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [setup, setSetup] = useState<EmployeeSetupResponse | null>(null);
  const [taxes, setTaxes] = useState<TaxSetupResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([employeeOnboardingGateway.getSetup(), employeeOnboardingGateway.getTaxes()])
      .then(([s, t]) => {
        if (cancelled) return;
        setSetup(s);
        setTaxes(t);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(friendlyErrorMessage(err, 'We could not load your tax information. Please try again.'));
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  /** Re-reads the saved tax state after a save. A failed re-read keeps the last known state; the save itself already succeeded. */
  const refresh = useCallback(async () => {
    try {
      setTaxes(await employeeOnboardingGateway.getTaxes());
    } catch {
      // Keep what is on screen; the next load will correct it.
    }
  }, []);

  const index = TAX_SECTIONS.findIndex((s) => s.slug === section);
  if (index < 0) return <Navigate to={taxSectionPath('federal')} replace />;

  const navigation = (reachableIndex: number): LayoutNavigation => ({
    ariaLabel: 'Taxes & Exemptions sections',
    items: TAX_SECTIONS.map((s) => ({ key: s.slug, label: s.label, to: taxSectionPath(s.slug) })),
    activeKey: TAX_SECTIONS[index]!.slug,
    reachableIndex,
  });

  if (loadError) {
    return (
      <OnboardingLayout activeStep={2} navigation={navigation(index)}>
        <div className="eo-panel">
          <p className="eo-banner eo-banner--error" role="alert">
            {loadError}
          </p>
          <button
            type="button"
            className="eo-btn eo-btn--primary"
            onClick={() => {
              setLoadError(null);
              setAttempt((a) => a + 1);
            }}
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  if (!setup || !taxes) {
    return (
      <OnboardingLayout activeStep={2} navigation={navigation(index)}>
        <p className="eo-loading" role="status">
          Loading your tax information…
        </p>
      </OnboardingLayout>
    );
  }

  // Step 1 must be finished first: the states below come from the addresses entered there.
  const step1 = reachableSectionIndex(setup);
  if (step1 < ONBOARDING_SECTIONS.length - 1) return <Navigate to={sectionPath(ONBOARDING_SECTIONS[step1]!.slug)} replace />;

  const reachable = reachableTaxIndex(taxes);
  if (index > reachable) return <Navigate to={taxSectionPath(TAX_SECTIONS[reachable]!.slug)} replace />;

  const def = TAX_SECTIONS[index]!;
  const Section = SECTION_COMPONENTS[def.slug];
  const nextDef = TAX_SECTIONS[index + 1];
  const prevDef = TAX_SECTIONS[index - 1];

  return (
    <OnboardingLayout activeStep={2} navigation={navigation(reachable)}>
      <Section
        key={def.slug}
        taxes={taxes}
        refresh={refresh}
        next={() => navigate(nextDef ? taxSectionPath(nextDef.slug) : DOCUMENTS_PLACEHOLDER_PATH)}
        back={() => navigate(prevDef ? taxSectionPath(prevDef.slug) : sectionPath('payroll-details'))}
      />
    </OnboardingLayout>
  );
}
